import { Link, useNavigate } from 'react-router-dom';
import { Bell, LogOut, Menu, Search as SearchIcon, X, Shield, Globe } from 'lucide-react';
import { Logo } from './Logo';
import { useAuth } from '@/lib/auth';
import { useLang } from '@/lib/i18n';
import { LANGS } from '@/lib/translations';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { useCallback, useEffect, useState } from 'react';

const initials = (u) => `${(u?.first_name || '?')[0]}${(u?.last_name || '')[0] || ''}`.toUpperCase();

const LangSwitcher = ({ compact = false }) => {
  const { lang, setLang } = useLang();
  return (
    <div className={`flex items-center rounded-full border border-[#E2E8F0] bg-[#F7FAFF] p-0.5 ${compact ? '' : 'ml-1'}`} data-testid="lang-switcher">
      {LANGS.map((l) => (
        <button
          key={l.code}
          onClick={() => setLang(l.code)}
          className={`px-2 py-1 rounded-full text-[11px] font-bold transition-colors duration-150 ${lang === l.code ? 'bg-[#1677F2] text-white' : 'text-slate-500 hover:text-[#1677F2]'}`}
          data-testid={`lang-btn-${l.code}`}
          title={l.full}
        >
          {l.label}
        </button>
      ))}
    </div>
  );
};

export const Header = () => {
  const { user, logout } = useAuth();
  const { t } = useLang();
  const nav = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [unread, setUnread] = useState(0);

  const loadUnread = useCallback(() => {
    if (!user) return;
    api.get('/notifications/mine').then((r) => setUnread(r.data.unread || 0)).catch(() => {});
  }, [user]);

  useEffect(() => {
    loadUnread();
    const interval = setInterval(loadUnread, 30000);
    window.addEventListener('jobkongo:notifs-read', loadUnread);
    return () => {
      clearInterval(interval);
      window.removeEventListener('jobkongo:notifs-read', loadUnread);
    };
  }, [loadUnread]);

  return (
    <header className="sticky top-0 z-30 bg-white/95 backdrop-blur border-b border-[#E2E8F0]" data-testid="main-header">
      <div className="max-w-7xl mx-auto px-4 md:px-6 h-14 flex items-center justify-between gap-3">
        <Link to="/" className="flex-shrink-0 flex items-center" data-testid="header-logo-link">
          <Logo size="md" variant="default" />
          <span className="hidden sm:inline ml-2 font-bold tracking-tight text-[#0E3F80] text-lg" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
            Job<span className="text-[#1677F2]">Kongo</span>
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-6 text-sm font-medium text-slate-700">
          <Link to="/search" className="hover:text-[#1677F2]" data-testid="header-search-link">{t('nav.search')}</Link>
          <Link to="/how-it-works" className="hover:text-[#1677F2]" data-testid="header-how-link">{t('nav.how')}</Link>
          <Link to="/trust" className="hover:text-[#1677F2]" data-testid="header-trust-link">{t('nav.trust')}</Link>
          <Link to="/ambassadors" className="hover:text-[#1677F2]" data-testid="header-ambassadors-link">{t('nav.ambassadors')}</Link>
          <Link to="/faq" className="hover:text-[#1677F2]" data-testid="header-faq-link">{t('nav.faq')}</Link>
        </nav>

        <div className="flex items-center gap-2">
          <div className="hidden md:block">
            <LangSwitcher />
          </div>
          <Link to="/search" className="md:hidden p-2 rounded-lg hover:bg-[#EAF3FF] text-slate-700" data-testid="header-mobile-search-btn">
            <SearchIcon size={20} />
          </Link>

          {user ? (
            <>
              <Link to="/notifications" className="relative p-2 rounded-lg hover:bg-[#EAF3FF] text-slate-700" data-testid="header-notifications-btn">
                <Bell size={18} />
                {unread > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 h-4 min-w-4 px-0.5 rounded-full bg-[#CE1021] text-white text-[9px] font-bold flex items-center justify-center" data-testid="header-notifications-badge">
                    {unread > 9 ? '9+' : unread}
                  </span>
                )}
              </Link>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="flex items-center gap-2 rounded-full hover:bg-[#EAF3FF] p-1" data-testid="header-user-menu">
                    <Avatar className="h-8 w-8">
                      {user.photo_base64 && <AvatarImage src={user.photo_base64} alt="" />}
                      <AvatarFallback className="bg-[#1677F2] text-white text-xs font-semibold">{initials(user)}</AvatarFallback>
                    </Avatar>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <div className="px-3 py-2">
                    <p className="font-semibold text-sm">{user.first_name} {user.last_name}</p>
                    <p className="text-xs text-slate-500 capitalize">{user.role}</p>
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => nav('/dashboard')} data-testid="menu-dashboard">{t('nav.dashboard')}</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => nav('/profile')} data-testid="menu-profile">{t('nav.profile')}</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => nav('/messages')} data-testid="menu-messages">{t('nav.messages')}</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => nav('/notifications')} data-testid="menu-notifications">{t('nav.notifications')}</DropdownMenuItem>
                  {user.role === 'admin' && (
                    <DropdownMenuItem onClick={() => nav('/admin')} data-testid="menu-admin">
                      <Shield size={14} className="mr-2" />{t('nav.admin')}
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => { logout(); nav('/'); }} data-testid="menu-logout">
                    <LogOut size={14} className="mr-2" /> {t('nav.logout')}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </>
          ) : (
            <div className="hidden md:flex items-center gap-2">
              <Button variant="ghost" asChild data-testid="header-login-btn">
                <Link to="/login">{t('nav.login')}</Link>
              </Button>
              <Button asChild className="bg-[#1677F2] hover:bg-[#0F5FCC]" data-testid="header-signup-btn">
                <Link to="/signup">{t('nav.signup')}</Link>
              </Button>
            </div>
          )}

          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <button className="md:hidden p-2 rounded-lg hover:bg-[#EAF3FF] text-slate-700" data-testid="header-mobile-menu-btn">
                {mobileOpen ? <X size={20} /> : <Menu size={20} />}
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="w-72">
              <div className="py-6 space-y-2">
                <div className="flex items-center gap-2 px-3 pb-2">
                  <Globe size={16} className="text-slate-500" />
                  <LangSwitcher compact />
                </div>
                <Link to="/search" onClick={() => setMobileOpen(false)} className="block px-3 py-2 rounded-lg hover:bg-[#EAF3FF] font-medium">{t('nav.search')}</Link>
                <Link to="/how-it-works" onClick={() => setMobileOpen(false)} className="block px-3 py-2 rounded-lg hover:bg-[#EAF3FF] font-medium">{t('nav.how')}</Link>
                <Link to="/trust" onClick={() => setMobileOpen(false)} className="block px-3 py-2 rounded-lg hover:bg-[#EAF3FF] font-medium">{t('nav.trust')}</Link>
                <Link to="/ambassadors" onClick={() => setMobileOpen(false)} className="block px-3 py-2 rounded-lg hover:bg-[#EAF3FF] font-medium">{t('nav.ambassadors')}</Link>
                <Link to="/faq" onClick={() => setMobileOpen(false)} className="block px-3 py-2 rounded-lg hover:bg-[#EAF3FF] font-medium">{t('nav.faq')}</Link>
                <Link to="/blog" onClick={() => setMobileOpen(false)} className="block px-3 py-2 rounded-lg hover:bg-[#EAF3FF] font-medium">Blog & conseils</Link>
                {!user && (
                  <div className="pt-4 space-y-2">
                    <Button asChild className="w-full bg-[#1677F2] hover:bg-[#0F5FCC]" onClick={() => setMobileOpen(false)}>
                      <Link to="/signup">{t('nav.signup')}</Link>
                    </Button>
                    <Button asChild variant="outline" className="w-full" onClick={() => setMobileOpen(false)}>
                      <Link to="/login">{t('nav.login')}</Link>
                    </Button>
                  </div>
                )}
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};
